import { type TypedDocumentNode, useQuery } from '@apollo/client'
import { Clear } from '@mui/icons-material'
import {
    Autocomplete,
    type AutocompleteProps,
    type AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { RosterType } from '__generated__/graphql'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import { MinimalCreationDateFilter } from './MinimalCreationDateFilter'
import { useMemo, useState, type FC } from 'react'
import { FilterWrapper } from 'recruiter/components/FilterWrapper'
import { filterCheckboxSx, filterCheckboxLabelSx } from 'recruiter/filterStyles'
import {
    GetFundSizeBucketOptions,
    GetIndustryOptions,
    GetSectorOptions,
    GetVisaStatusOptions,
} from 'recruiter/graphql/companies.gql'
import { type RosterFilterState } from 'shared/hooks/rosterFilterState'
import CompaniesAutocompleteMultiple from 'shared/components/CompaniesAutocomplete/CompaniesAutocompleteMultiple'
import OfficeLocationsAutocompleteMultiple from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocompleteMultiple'
import { GetRecruitersByUserIds } from 'shared/components/RecruitersAutocomplete/RecruitersAutocomplete.gql'
import RecruitersAutocompleteMultiple from 'shared/components/RecruitersAutocomplete/RecruitersAutocompleteMultiple'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import { GetRosterStartDateOptions } from 'shared/graphql/startDates.gql'
import { GetTitleOptions } from 'shared/graphql/titles.gql'
import { getFullName } from 'shared/utils'
import { toggleChipSx } from 'recruiter/components/CandidateTableToolbar/minimalFilterStyles'
import { minimalInputSx, minimalTagSx } from './styles'

const renderMinimalTags =
    <T,>(getLabel: (option: T) => string) =>
    (value: T[], getTagProps: AutocompleteRenderGetTagProps) =>
        value.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                {value.map((option, index) => {
                    const { key, onDelete, ...tagProps } = getTagProps({
                        index,
                    })
                    return (
                        <Typography
                            key={key}
                            {...tagProps}
                            onClick={onDelete}
                            component="span"
                            sx={minimalTagSx}
                        >
                            {getLabel(option)}
                            <Clear sx={{ fontSize: '0.625rem' }} />
                        </Typography>
                    )
                })}
            </Box>
        ) : null

type FilterAutocompleteProps<T> = Omit<
    AutocompleteProps<T, true, false, false>,
    | 'renderInput'
    | 'renderTags'
    | 'multiple'
    | 'size'
    | 'open'
    | 'onOpen'
    | 'onClose'
> & {
    getTagLabel: (option: T) => string
    onSearchChange?: (value: string) => void
}

function FilterAutocomplete<T>({
    getTagLabel,
    onSearchChange,
    ...props
}: FilterAutocompleteProps<T>) {
    const [open, setOpen] = useState(false)

    return (
        <Autocomplete
            multiple
            size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
                if (reason !== 'selectOption' && reason !== 'removeOption') {
                    setOpen(false)
                }
            }}
            onInputChange={
                onSearchChange ? (_, value) => onSearchChange(value) : undefined
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Type to search..."
                    sx={minimalInputSx}
                />
            )}
            renderTags={renderMinimalTags(getTagLabel)}
            renderOption={({ key, ...optionProps }, option, { selected }) => (
                <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        minHeight: 'unset',
                        bgcolor: selected ? 'primary.50' : 'inherit',
                        borderLeft: selected ? '3px solid' : 'none',
                        borderLeftColor: 'primary.main',
                    }}
                >
                    {props.getOptionLabel?.(option) ?? String(option)}
                </Box>
            )}
            {...props}
        />
    )
}

const MinimalRecruiterFilter: FC<{
    label: string
    value: string[]
    onChange: (ids: string[]) => void
}> = ({ label, value, onChange }) => {
    const ids = value

    const { data: selectedData, loading: selectedLoading } = useQuery(
        GetRecruitersByUserIds,
        { variables: { ids }, skip: ids.length === 0 }
    )

    const selectedRecruiters = selectedData?.recruitersByUserId ?? []

    return (
        <FilterWrapper label={label}>
            <RecruitersAutocompleteMultiple
                label=""
                size="small"
                placeholder="Type to search..."
                loading={selectedLoading}
                value={selectedRecruiters}
                onChange={(_, newValues) => {
                    onChange(newValues.map(({ userId }) => userId))
                }}
                sx={minimalInputSx}
                renderTags={renderMinimalTags((opt) => getFullName(opt.user))}
                renderOption={(
                    { key, ...optionProps },
                    option,
                    { selected }
                ) => (
                    <Box
                        component="li"
                        key={key}
                        {...optionProps}
                        sx={{
                            fontSize: '0.75rem',
                            py: 0.5,
                            minHeight: 'unset',
                            bgcolor: selected ? 'primary.50' : 'inherit',
                            borderLeft: selected ? '3px solid' : 'none',
                            borderLeftColor: 'primary.main',
                        }}
                    >
                        {getFullName(option?.user ?? {})}
                    </Box>
                )}
            />
        </FilterWrapper>
    )
}

const MinimalCompanyFilter: FC<{
    value: string[]
    onChange: (ids: string[]) => void
}> = ({ value, onChange }) => {
    const [showOnlyTopTierCompanies, setShowOnlyTopTierCompanies] =
        useState(false)
    const companyIds = value

    const { data, loading } = useQuery(GetCompaniesById, {
        variables: { ids: companyIds },
    })

    return (
        <FilterWrapper
            label="Company"
            headerActions={
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showOnlyTopTierCompanies}
                            onChange={() =>
                                setShowOnlyTopTierCompanies(
                                    !showOnlyTopTierCompanies
                                )
                            }
                            sx={filterCheckboxSx}
                        />
                    }
                    label="Top Tier"
                    sx={filterCheckboxLabelSx}
                />
            }
        >
            <CompaniesAutocompleteMultiple
                size="small"
                label=""
                onChange={(_, newValues) =>
                    onChange(newValues.map(({ id }) => id))
                }
                getOptionLabel={({ value }) => value}
                placeholder="Type to search..."
                value={data?.companiesById || []}
                loading={loading}
                topTierOnly={showOnlyTopTierCompanies}
                sx={minimalInputSx}
                renderTags={renderMinimalTags((opt) => opt.value)}
                ListboxProps={{
                    sx: {
                        '& .MuiAutocomplete-option': {
                            fontSize: '0.75rem',
                            py: 0.5,
                            minHeight: 'unset',
                            '&[aria-selected="true"]': {
                                bgcolor: 'primary.50',
                                borderLeft: '3px solid',
                                borderLeftColor: 'primary.main',
                            },
                        },
                    },
                }}
            />
        </FilterWrapper>
    )
}

interface MinimalOptionsFilterProps<TData> {
    label: string
    value: string[]
    onChange: (ids: string[]) => void
    query: TypedDocumentNode<TData, Record<string, never>>
    getOptions: (
        data: TData | undefined
    ) => Array<{ id: string; value: string }>
}

function MinimalOptionsFilter<TData>({
    label,
    value,
    onChange,
    query,
    getOptions,
}: MinimalOptionsFilterProps<TData>) {
    const { data, loading } = useQuery(query)

    const options = getOptions(data) ?? []
    const ids = value
    const selectedOptions = options.filter(({ id }) => ids.includes(id))

    return (
        <FilterWrapper label={label}>
            <FilterAutocomplete
                loading={loading}
                options={options}
                value={selectedOptions}
                getOptionLabel={({ value }) => value}
                getTagLabel={({ value }) => value}
                getOptionKey={({ id }) => id}
                onChange={(_, newValue) =>
                    onChange(newValue.map(({ id }) => id))
                }
            />
        </FilterWrapper>
    )
}

const getStateFromLocation = (locationValue: string): string => {
    const parts = locationValue.split(', ')
    return parts.length > 1 ? parts[parts.length - 1] : 'Other'
}

const isGroupMarker = (v: string) => v.startsWith(', ')

const MinimalLocationFilter: FC<{
    value: string[]
    onChange: (values: string[]) => void
}> = ({ value: locationValues, onChange }) => {
    const selectedGroups = locationValues.filter(isGroupMarker)
    const selectedIndividuals = locationValues.filter((v) => !isGroupMarker(v))

    const selectedLocations = [
        ...selectedGroups.map((g) => ({ id: g, value: g })),
        ...selectedIndividuals.map((v) => ({ id: v, value: v })),
    ]

    const handleGroupClick = (state: string) => {
        const groupMarker = `, ${state}`
        const isSelected = selectedGroups.includes(groupMarker)

        if (isSelected) {
            onChange(locationValues.filter((v) => v !== groupMarker))
        } else {
            const withoutStateIndividuals = locationValues.filter(
                (v) => isGroupMarker(v) || getStateFromLocation(v) !== state
            )
            onChange([...withoutStateIndividuals, groupMarker])
        }
    }

    const handleChange = (
        _: unknown,
        newValue: Array<{ id: string; value: string }>
    ) => {
        const newGroups = newValue
            .filter((v) => isGroupMarker(v.value))
            .map((v) => v.value)
        const newIndividuals = newValue
            .filter((v) => !isGroupMarker(v.value))
            .map((v) => v.value)

        const newlyAddedIndividuals = newIndividuals.filter(
            (v) => !selectedIndividuals.includes(v)
        )

        let updatedGroups = [...newGroups]
        for (const added of newlyAddedIndividuals) {
            const state = getStateFromLocation(added)
            const groupMarker = `, ${state}`
            if (updatedGroups.includes(groupMarker)) {
                updatedGroups = updatedGroups.filter((g) => g !== groupMarker)
            }
        }

        onChange([...updatedGroups, ...newIndividuals])
    }

    const renderLocationTags = (
        tagValues: Array<{ id: string; value: string }>,
        getTagProps: AutocompleteRenderGetTagProps
    ) =>
        tagValues.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                {tagValues.map((option, index) => {
                    const isGroup = isGroupMarker(option.value)
                    const displayValue = isGroup
                        ? option.value.slice(2)
                        : option.value
                    const { key, onDelete, ...tagProps } = getTagProps({
                        index,
                    })

                    return (
                        <Typography
                            key={option.id}
                            {...tagProps}
                            onClick={
                                isGroup
                                    ? () => handleGroupClick(displayValue)
                                    : onDelete
                            }
                            component="span"
                            sx={minimalTagSx}
                        >
                            {displayValue}
                            <Clear sx={{ fontSize: '0.625rem' }} />
                        </Typography>
                    )
                })}
            </Box>
        ) : null

    const transformOptions = (
        options: Array<{ id: string; value: string }>
    ) => {
        const result: Array<{ id: string; value: string }> = []
        let currentState: string | null = null

        for (const opt of options) {
            const state = getStateFromLocation(opt.value)
            if (state !== currentState) {
                // Skip "Other" group header - it's not a meaningful geographic selector
                if (state !== 'Other') {
                    result.push({ id: `header-${state}`, value: `, ${state}` })
                }
                currentState = state
            }
            result.push(opt)
        }
        return result
    }

    return (
        <FilterWrapper label="Location">
            <OfficeLocationsAutocompleteMultiple
                includeSentinels
                size="small"
                label=""
                onChange={handleChange}
                getOptionLabel={(option) =>
                    isGroupMarker(option.value)
                        ? option.value.slice(2)
                        : option.value
                }
                placeholder="Type to search..."
                value={selectedLocations}
                sx={minimalInputSx}
                renderTags={renderLocationTags}
                transformOptions={transformOptions}
                isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                }
                renderOption={({ key, ...optionProps }, option) => {
                    const isHeader = isGroupMarker(option.value)
                    const displayValue = isHeader
                        ? option.value.slice(2)
                        : option.value
                    const isSelected = isHeader
                        ? selectedGroups.includes(option.value)
                        : selectedIndividuals.includes(option.value)

                    return (
                        <Box
                            component="li"
                            key={option.id}
                            {...optionProps}
                            onClick={
                                isHeader
                                    ? (e) => {
                                          e.stopPropagation()
                                          handleGroupClick(displayValue)
                                      }
                                    : optionProps.onClick
                            }
                            sx={{
                                fontSize: isHeader ? '0.6875rem' : '0.75rem',
                                fontWeight: isHeader ? 600 : 400,
                                py: isHeader ? 0.25 : 0.5,
                                pl: isHeader ? 1 : 3,
                                letterSpacing: isHeader ? '0.05em' : 'normal',
                                bgcolor: isSelected
                                    ? 'primary.50'
                                    : isHeader
                                      ? 'grey.50'
                                      : 'inherit',
                                borderLeft: isSelected ? '3px solid' : 'none',
                                borderLeftColor: 'primary.main',
                                minHeight: 'unset',
                            }}
                        >
                            {displayValue}
                        </Box>
                    )
                }}
            />
        </FilterWrapper>
    )
}

interface ProcessStatusOption {
    id: string
    label: string
    type: 'group' | 'status'
    groupLabel: string
}

const MinimalProcessStatusFilter: FC<{
    filters: RosterFilterState
    updateFilters: (patch: Partial<RosterFilterState>) => void
}> = ({ filters, updateFilters }) => {
    const [open, setOpen] = useState(false)
    const { data, loading } = useQuery(GetProcessStatuses)

    const allOptions: ProcessStatusOption[] = useMemo(() => {
        const statuses = data?.processStatuses ?? []
        const grouped = new Map<
            string,
            { groupId: string; groupLabel: string; statuses: typeof statuses }
        >()

        for (const ps of statuses) {
            const groupId = ps.processStatusSummaryGroup?.id ?? ''
            const groupLabel = ps.processStatusSummaryGroup?.label ?? 'No Group'
            if (!grouped.has(groupId)) {
                grouped.set(groupId, { groupId, groupLabel, statuses: [] })
            }
            grouped.get(groupId)!.statuses.push(ps)
        }

        const result: ProcessStatusOption[] = []
        const sortedGroups = [...grouped.entries()].sort(([, a], [, b]) =>
            a.groupLabel.localeCompare(b.groupLabel)
        )

        for (const [
            groupId,
            { groupLabel, statuses: groupStatuses },
        ] of sortedGroups) {
            result.push({
                id: groupId,
                label: groupLabel,
                type: 'group',
                groupLabel,
            })
            for (const ps of groupStatuses) {
                result.push({
                    id: ps.id,
                    label: ps.statusLabel,
                    type: 'status',
                    groupLabel,
                })
            }
        }

        return result
    }, [data?.processStatuses])

    const selectedOptions = useMemo(() => {
        const statusIds = new Set(filters.processStatusIds)
        const groupIds = new Set(filters.processStatusGroupIds)
        return allOptions.filter(
            (opt) =>
                (opt.type === 'status' && statusIds.has(opt.id)) ||
                (opt.type === 'group' && groupIds.has(opt.id))
        )
    }, [allOptions, filters.processStatusIds, filters.processStatusGroupIds])

    const handleChange = (_: unknown, newValue: ProcessStatusOption[]) => {
        updateFilters({
            processStatusIds: newValue
                .filter((v) => v.type === 'status')
                .map((v) => v.id),
            processStatusGroupIds: newValue
                .filter((v) => v.type === 'group')
                .map((v) => v.id),
        })
    }

    return (
        <FilterWrapper label="Process Status">
            <Autocomplete
                multiple
                size="small"
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (
                        reason !== 'selectOption' &&
                        reason !== 'removeOption'
                    ) {
                        setOpen(false)
                    }
                }}
                options={allOptions}
                loading={loading}
                value={selectedOptions}
                onChange={handleChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                    option.id === value.id && option.type === value.type
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Type to search..."
                        sx={minimalInputSx}
                    />
                )}
                renderTags={renderMinimalTags<ProcessStatusOption>(
                    (opt) => opt.label
                )}
                renderOption={({ key, ...optionProps }, option) => {
                    const isGroup = option.type === 'group'
                    const isSelected = isGroup
                        ? filters.processStatusGroupIds.includes(option.id)
                        : filters.processStatusIds.includes(option.id)

                    return (
                        <Box
                            component="li"
                            key={key}
                            {...optionProps}
                            sx={{
                                fontSize: isGroup ? '0.6875rem' : '0.75rem',
                                fontWeight: isGroup ? 600 : 400,
                                py: isGroup ? 0.25 : 0.5,
                                pl: isGroup ? 1 : 3,
                                letterSpacing: isGroup ? '0.05em' : 'normal',
                                bgcolor: isSelected
                                    ? 'primary.50'
                                    : isGroup
                                      ? 'grey.50'
                                      : 'inherit',
                                borderLeft: isSelected ? '3px solid' : 'none',
                                borderLeftColor: 'primary.main',
                                minHeight: 'unset',
                            }}
                        >
                            {option.label}
                        </Box>
                    )
                }}
            />
        </FilterWrapper>
    )
}

const MinimalStartDateFilter: FC<{
    value: string[]
    onChange: (values: string[]) => void
}> = ({ value, onChange }) => {
    const { data, loading } = useQuery(GetRosterStartDateOptions)

    const options = data?.rosterStartDates ?? []
    const selectedOptions = options.filter((opt) => value.includes(opt))

    return (
        <FilterWrapper label="Start Date">
            <FilterAutocomplete
                loading={loading}
                options={options}
                value={selectedOptions}
                getOptionLabel={(opt) => opt}
                getTagLabel={(opt) => opt}
                onChange={(_, newValue) => onChange(newValue)}
            />
        </FilterWrapper>
    )
}

const typeOptions = Object.values(RosterType)

const MinimalTypeFilter: FC<{
    value: string | undefined
    onChange: (value: string | undefined) => void
}> = ({ value, onChange }) => {
    const selectedOptions = value ? typeOptions.filter((t) => t === value) : []

    return (
        <FilterWrapper label="Type">
            <FilterAutocomplete
                options={typeOptions}
                value={selectedOptions}
                getOptionLabel={(opt) => opt}
                getTagLabel={(opt) => opt}
                onChange={(_, newValue) =>
                    onChange(
                        newValue.length > 0
                            ? newValue[newValue.length - 1]
                            : undefined
                    )
                }
            />
        </FilterWrapper>
    )
}

interface FilterSectionProps {
    filters: RosterFilterState
    updateFilters: (patch: Partial<RosterFilterState>) => void
}

export const CompanyFilters: FC<FilterSectionProps> = ({
    filters,
    updateFilters,
}) => (
    <Stack gap={2}>
        <MinimalCompanyFilter
            value={filters.companyIds}
            onChange={(ids) => updateFilters({ companyIds: ids })}
        />
        <MinimalOptionsFilter
            label="Title"
            value={filters.titleIds}
            onChange={(ids) => updateFilters({ titleIds: ids })}
            query={GetTitleOptions}
            getOptions={(data) => data?.titles ?? []}
        />
        <FilterWrapper label="Consultants">
            <Stack direction="row" gap={0.5}>
                <Chip
                    label="Yes"
                    size="small"
                    onClick={() =>
                        updateFilters({
                            consultants:
                                filters.consultants === 'yes'
                                    ? undefined
                                    : 'yes',
                        })
                    }
                    sx={toggleChipSx(filters.consultants === 'yes')}
                />
                <Chip
                    label="No"
                    size="small"
                    onClick={() =>
                        updateFilters({
                            consultants:
                                filters.consultants === 'no' ? undefined : 'no',
                        })
                    }
                    sx={toggleChipSx(filters.consultants === 'no')}
                />
            </Stack>
        </FilterWrapper>
        <MinimalOptionsFilter
            label="Fund Size"
            value={filters.fundSizeIds}
            onChange={(ids) => updateFilters({ fundSizeIds: ids })}
            query={GetFundSizeBucketOptions}
            getOptions={(data) => data?.fundSizeBuckets ?? []}
        />
        <MinimalOptionsFilter
            label="Visa"
            value={filters.visaStatusIds}
            onChange={(ids) => updateFilters({ visaStatusIds: ids })}
            query={GetVisaStatusOptions}
            getOptions={(data) => data?.visaStatuses ?? []}
        />
        <MinimalOptionsFilter
            label="Industry"
            value={filters.industryIds}
            onChange={(ids) => updateFilters({ industryIds: ids })}
            query={GetIndustryOptions}
            getOptions={(data) => data?.industries ?? []}
        />
        <MinimalOptionsFilter
            label="Sector"
            value={filters.sectorIds}
            onChange={(ids) => updateFilters({ sectorIds: ids })}
            query={GetSectorOptions}
            getOptions={(data) => data?.sectors ?? []}
        />
        <MinimalLocationFilter
            value={filters.geoLocationText}
            onChange={(values) => updateFilters({ geoLocationText: values })}
        />
        <MinimalStartDateFilter
            value={filters.startDates}
            onChange={(values) => updateFilters({ startDates: values })}
        />
        <MinimalProcessStatusFilter
            filters={filters}
            updateFilters={updateFilters}
        />
        <MinimalTypeFilter
            value={filters.type}
            onChange={(type) => updateFilters({ type })}
        />
        <MinimalRecruiterFilter
            label="Internal Users"
            value={filters.internalUserIds}
            onChange={(ids) => updateFilters({ internalUserIds: ids })}
        />
        <MinimalRecruiterFilter
            label="Created By"
            value={filters.createdByUserIds}
            onChange={(ids) => updateFilters({ createdByUserIds: ids })}
        />
        <MinimalCreationDateFilter
            createdAtGte={filters.createdAtGte}
            createdAtLte={filters.createdAtLte}
            onChange={updateFilters}
        />
    </Stack>
)
